import React, { useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import PostModalContent from './PostModalContent';
import { useSinglePost } from '../../hooks/useSinglePost';
import useClickOutside from '../../hooks/useClickOutside';
import { IconButton } from '../ui';
import { useAppStore } from '../../store/useAppStore';
import { useAppTourStore } from '../../store/useAppTourStore';

const PostModal = () => {
  // Use custom hook for all logic
  const {
    socialAccounts,
    handleEditSubmit,
    handleScheduleSubmit,
    handleCancelPost,
    handleRegenerateSubmit,
    handleFeedbackSubmit,
    isOpen,
    closeModal,
    post,
  } = useSinglePost();
  const { business_name, logo } = useAppStore();
  const { tourFinished, run } = useAppTourStore();

  const disableClickOutside = useMemo(() => run && !tourFinished, [run, tourFinished]);
  // Use click outside hook to close modal
  const modalRef = useClickOutside(() => {
    closeModal();
  }, disableClickOutside);

  // Effect: manage body overflow on modal open/close
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Conditional rendering: don't render if modal not open or no post
  if (!isOpen || !post) {
    return null;
  }

  return (
    <div className='fixed inset-0 bg-black/50 backdrop-blur-sm z-30 flex items-center justify-center p-4'>
      <div
        ref={modalRef}
        className='relative rounded-2xl shadow-lg w-full lg:max-w-5xl h-full max-h-[90vh] md:h-auto flex flex-col overflow-hidden'
        style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}
      >
        <IconButton
          icon={X}
          onClick={() => {
            closeModal();
          }}
          variant='ghost'
          size='lg'
          tooltip='Close'
          tooltipPosition='bottom'
          className='absolute right-0 top-0 z-10 hover:rounded-full p-0 transition-colors'
        />
        <div
          id='modal-content'
          className='flex-1 overflow-y-auto h-full w-full max-w-5xl '
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Main Body - Grid Layout */}
          <PostModalContent
            post={post}
            onRegenerateSubmit={handleRegenerateSubmit}
            onFeedbackSubmit={handleFeedbackSubmit}
            socialAccounts={socialAccounts}
            onScheduleSubmit={handleScheduleSubmit}
            onCancelPost={handleCancelPost}
            onEditSubmit={handleEditSubmit}
            onClose={closeModal}
            businessName={business_name}
            logo={logo}
            showProductImage
          />
        </div>
      </div>
    </div>
  );
};

export default PostModal;
